<template>
    <v-container>
      <v-btn @click="$router.push('/')" icon="mdi-arrow-left" class="mb-4"></v-btn>
      <div class="d-flex align-center mb-4">
        <h1 class="flex-grow-1">Client Details</h1>
        <v-btn
          color="error"
          prepend-icon="mdi-delete"
          @click="showDeleteDialog = true"
        >
          Delete Client
        </v-btn>
      </div>
      
      <v-card class="mt-4">
        <v-card-text>
          <v-list>
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-domain" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>Company Name</v-list-item-title>
              <v-list-item-subtitle>{{ client?.name }}</v-list-item-subtitle>
            </v-list-item>
  
            <v-divider></v-divider>
  
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-account" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>Contact Name</v-list-item-title>
              <v-list-item-subtitle>{{ client?.contactName }}</v-list-item-subtitle>
            </v-list-item>
  
            <v-divider></v-divider>
  
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-email" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>Contact Email</v-list-item-title>
              <v-list-item-subtitle>{{ client?.contactEmail }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-key" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>Token Status</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  :color="getTokenStatusColor(client?.tokenStatus)"
                  size="small"
                  variant="elevated"
                  class="mt-1 font-weight-medium"
                >
                  {{ client?.tokenStatus || 'Unknown' }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-link" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>Landing Page URL</v-list-item-title>
              <v-list-item-subtitle class="d-flex align-center mt-1">
                <span class="text-truncate">{{ generatedUrl }}</span>
                <v-btn
                  icon="mdi-content-copy"
                  size="small"
                  variant="text"
                  class="ml-2"
                  @click="copyUrl"
                ></v-btn>
              </v-list-item-subtitle>
              <v-snackbar
                v-model="showCopiedAlert"
                color="success"
                timeout="2000"
              >
                URL copied to clipboard!
              </v-snackbar>
            </v-list-item>

            <v-divider></v-divider>

            <ErrorsView :errors="client?.errors" @error-deleted="fetchClient" />
          </v-list>
        </v-card-text>
      </v-card>

      <v-btn
        color="primary"
        class="mt-4"
        :loading="downloadLoading"
        :disabled="downloadLoading"
        prepend-icon="mdi-download"
        @click="downloadReport"
      >
        Download Report
      </v-btn>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500px">
        <v-card>
          <v-card-title>Delete Client</v-card-title>
          <v-card-text>
            Are you sure you want to delete this client? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="text"
              @click="showDeleteDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              variant="text"
              :loading="deleteLoading"
              @click="deleteClient"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar
        v-model="showErrorSnackbar"
        color="error"
        :timeout="3000"
        location="top"
      >
        {{ errorMessage }}
      </v-snackbar>
    </v-container>
  </template>
  
  <script setup lang="ts">
  import { ref, onMounted, watch, inject, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { api } from '@/services/api';
  import type Client from '@/models/client';
  import ErrorsView from '@/views/ErrorsView.vue';
  
  const route = useRoute();
  const router = useRouter();
  const client = ref<Client | null>(null);
  const showDeleteDialog = ref(false);
  const deleteLoading = ref(false);
  const showCopiedAlert = ref(false);
  const downloadLoading = ref(false);
  const showErrorSnackbar = ref(false);
  const errorMessage = ref('');
  const generatedUrl = computed(() => {
    const baseUrl = process.env.VUE_APP_LANDING_PAGE_URL;
    console.log(baseUrl);
    return `${baseUrl}${route.params.id}`;
  });
  
  // Inject the refresh method
  const refreshClients = inject('refreshClients') as () => Promise<void>;
  
  const getTokenStatusColor = (status?: string) => {
    switch (status) {
      case 'Active':
        return 'green-darken-1';
      case 'Expired':
      case 'Inactive':
      case 'Disabled':
      case 'Invalid':
      case 'Revoked':
        return 'red-darken-2';
      case 'PendingAuthorization':
        return 'orange-darken-1';
      default:
        return 'grey-darken-1';
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl.value);
      showCopiedAlert.value = true;
      setTimeout(() => {
        showCopiedAlert.value = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };
  
  const fetchClient = async () => {
    try {
      // todo aram implement no clientId in params OR client not found
      const clientId = route.params.id as string;
      const response = await api.getClient(clientId)
      client.value = response;
    } catch (error) {
      console.error('Error fetching client:', error);
    }
  };
  
  // Watch for changes in the route params
  watch(
    () => route.params.id,
    (newId) => {
      if (newId) {
        fetchClient();
      }
    }
  );
  
  onMounted(() => {
    fetchClient();
  });
  
  // Add delete client method
  const deleteClient = async () => {
    try {
      deleteLoading.value = true;
      const clientId = route.params.id as string;
      await api.deleteClient(clientId);
      
      // Refresh the clients list
      await refreshClients();
      
      // Close the dialog and redirect to home
      showDeleteDialog.value = false;
      router.push('/');
    } catch (error) {
      console.error('Error deleting client:', error);
      // You might want to show an error message to the user here
    } finally {
      deleteLoading.value = false;
    }
  };

  const downloadReport = async () => {
    try {
      downloadLoading.value = true;
      const clientId = route.params.id as string;
      const blob = await api.downloadReport(clientId);
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateTime = now.toISOString().slice(0, 16).replace('T', '_');
      link.download = `payment-report-${client?.value?.name}-${dateTime}.csv`;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      // Refresh client data to get updated errors
      await fetchClient();
      
      // Show error notification
      errorMessage.value = 'Failed to download report';
      showErrorSnackbar.value = true;
    } finally {
      downloadLoading.value = false;
    }
  };
</script>